import appconfig from "../AppConfig";
import { ICartItem, IPicture } from "../models";
import {
  ADDITIONAL_EQUIPMENT_CATEGORY,
  ADDITIONAL_EQUIPMENT_IDs,
  ID_BRANDED_BAG,
  ID_COLOR_PACK,
  ID_EASEL_ALUM,
  ID_EASEL_WOOD,
  ID_FRAME,
  ID_FRAME_ASSEMBLED,
  ID_SHIPPING,
  ID_SHIPPING_BIH,
  KIDS_CATEGORY,
  REGULAR_PIC_CATEGORIES,
} from "./constants";

export const isEmpty = (obj: any) => Object.keys(obj).length === 0;

export const debug = (obj: any, sender?: string) => {
  if (appconfig.debug) {
    if (sender) {
      console.log("====" + sender + "====");
    }
    console.log(obj);
  }
};

export const getLocalBool = (key: string): boolean => {
  const storageString = localStorage.getItem(key);
  return storageString ? JSON.parse(storageString) : false;
};

export const getLocalObject = (key: string) => {
  const storageString = localStorage.getItem(key);
  let result = undefined;
  try {
    result = storageString ? JSON.parse(storageString) : undefined;
  } catch (e) {
    console.log(e);
  }
  return result ? result : undefined;
};

export const isAdditionalEquipment = (pictureId: number): boolean => {
  return ADDITIONAL_EQUIPMENT_IDs.includes(pictureId);
};

export const getEquipmentFileName = (pictureId: number): string => {
  switch (pictureId) {
    case ID_FRAME:
      return "FRAME";
    case ID_FRAME_ASSEMBLED:
      return "FRAME";
    case ID_EASEL_WOOD:
      return "EASEL-WOOD";
    case ID_EASEL_ALUM:
      return "EASEL-ALUMINIUM";
    case ID_COLOR_PACK:
      return "COLOR-PACK";
    case ID_BRANDED_BAG:
      return "BRANDED-BAG";
    default:
      return "ERROR";
  }
};

export const calcTotal = (items: ICartItem[]): number => {
  if (!items || items.length === 0) {
    return 0;
  }
  let sum = 0;
  items.forEach((item) => {
    if (!item.isReservation) {
      sum = sum + item.price * item.quantity;
    }
  });
  return sum;
};

export const pic = (id: number, allPictures: IPicture[]): IPicture =>
  (allPictures && allPictures.find((p) => p.id === id)) || ({} as IPicture);

export const getCode = (picture_id: number, allPictures: IPicture[]) => {
  if (picture_id === ID_FRAME) {
    return "RAM";
  } else if (picture_id === ID_FRAME_ASSEMBLED) {
    return "RAM - SKLOPLJEN";
  } else if (picture_id === ID_EASEL_WOOD) {
    return "DRVENI ŠTAFELAJ";
  } else if (picture_id === ID_EASEL_ALUM) {
    return "ALUMINIJUMSKI ŠTAFELAJ";
  } else if (picture_id === ID_COLOR_PACK) {
    return "SET BOJA";
  } else if (picture_id === ID_BRANDED_BAG) {
    return "BRENDIRANA KESA";
  }
  return pic(picture_id, allPictures).code;
};

const getTypicalPriceForCategories = (
  pictures: IPicture[],
  categories: number[]
): number | null => {
  const relevant = pictures.filter(
    (p) => p.price && categories.includes(p.category)
  );

  if (relevant.length === 0) return null;

  // Count occurrences of each price
  const priceCounts = new Map<number, number>();
  for (const p of relevant) {
    priceCounts.set(p.price, (priceCounts.get(p.price) ?? 0) + 1);
  }

  // Find the price with the most occurrences
  let typicalPrice = 0;
  let maxCount = 0;
  priceCounts.forEach((count, price) => {
    if (count > maxCount || (count === maxCount && price > typicalPrice)) {
      // If tie → pick higher price (optional rule)
      maxCount = count;
      typicalPrice = price;
    }
  });

  return typicalPrice;
};

// Reusable helper to filter equipment pictures once
const getEquipmentMap = (allPictures: IPicture[]) => {
  return new Map(
    allPictures
      .filter((p) => p.category === ADDITIONAL_EQUIPMENT_CATEGORY)
      .map((p) => [p.id, p])
  );
};

// Return equipment prices
export const getEquipmentPrice = (allPictures: IPicture[]) => {
  const map = getEquipmentMap(allPictures);
  return {
    frame: map.get(ID_FRAME)?.price || 0,
    frameAssembled: map.get(ID_FRAME_ASSEMBLED)?.price || 0,
    easelA: map.get(ID_EASEL_ALUM)?.price || 0,
    easelW: map.get(ID_EASEL_WOOD)?.price || 0,
    colorPack: map.get(ID_COLOR_PACK)?.price || 0,
    brandedBag: map.get(ID_BRANDED_BAG)?.price || 0,
    shipping: map.get(ID_SHIPPING)?.price || 0,
    shippingBih: map.get(ID_SHIPPING_BIH)?.price || 0,
    typicalPriceRegular:
      getTypicalPriceForCategories(allPictures, REGULAR_PIC_CATEGORIES) || 0,
    typicalPriceKids:
      getTypicalPriceForCategories(allPictures, [KIDS_CATEGORY]) || 0,
  };
};

// Return equipment statuses
export const getEquipmentStatus = (allPictures: IPicture[]) => {
  const map = getEquipmentMap(allPictures);
  return {
    frame: map.get(ID_FRAME)?.status,
    frameAssembled: map.get(ID_FRAME_ASSEMBLED)?.status,
    easelA: map.get(ID_EASEL_ALUM)?.status,
    easelW: map.get(ID_EASEL_WOOD)?.status,
    colorPack: map.get(ID_COLOR_PACK)?.status,
    brandedBag: map.get(ID_BRANDED_BAG)?.status,
  };
};

/** "1 rasklopljen ram, 1 sklopljen ram" for a picture order line; "" without frames. */
export const framesText = (
  frames?: number | null,
  framesAssembled?: number | null
) =>
  [
    frames ? `${frames} rasklopljen ram` : "",
    framesAssembled ? `${framesAssembled} sklopljen ram` : "",
  ]
    .filter(Boolean)
    .join(", ");

/**
 * Admin check: frames chosen per picture must match the FRAME / FRAME-ASSEMBLED lines
 * (plain frames may also be ordered without a picture). Old orders without the per-picture
 * choice are skipped. Returns the warning, or "" when it adds up.
 */
export const framesMismatch = (items: ICartItem[]) => {
  if (!items.some((i) => i.frames != null || i.framesAssembled != null)) {
    return "";
  }
  const charged = items.filter((i) => !i.isReservation);
  const sum = (list: ICartItem[], f: (i: ICartItem) => number) =>
    list.reduce((total, i) => total + f(i), 0);
  const frames = sum(charged, (i) => i.frames || 0);
  const assembled = sum(charged, (i) => i.framesAssembled || 0);
  const frameLine = sum(
    charged.filter((i) => i.picture_id === ID_FRAME),
    (i) => i.quantity
  );
  const assembledLine = sum(
    charged.filter((i) => i.picture_id === ID_FRAME_ASSEMBLED),
    (i) => i.quantity
  );
  return frameLine < frames || assembledLine !== assembled
    ? `Ramovi uz slike (${frames} rasklopljen, ${assembled} sklopljen) ne odgovaraju stavkama Blind ram (${frameLine}) i Blind ram - sklopljen (${assembledLine}).`
    : "";
};

export const capitalizeFirstLetter = (str: string) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};
